import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';

interface Recommendation {
  title: string;
  description: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  category: string;
  implementation: string;
  impact: string;
  effort: 'low' | 'medium' | 'high';
}

interface AIReadinessAnalysis {
  success: boolean;
  score: number;
  recommendations: Recommendation[];
  pageInfo: any;
  analysis: {
    timestamp: string;
    model: string;
    totalRecommendations: number;
    criticalIssues: number;
    highPriority: number;
    mediumPriority: number;
    lowPriority: number;
  };
}

interface AIReadinessPanelProps {
  pageId: string;
  pageTitle: string;
  pageUrl: string;
  onClose: () => void;
}

const AIReadinessPanel: React.FC<AIReadinessPanelProps> = ({
  pageId,
  pageTitle,
  pageUrl,
  onClose
}) => {
  const [analysis, setAnalysis] = useState<AIReadinessAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState<string | null>(null);

  useEffect(() => {
    analyzePage();
  }, [pageId]);

  const analyzePage = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/ai-readiness/analyze/${pageId}`);
      setAnalysis(response.data.analysis);
    } catch (error) {
      console.error('AI Readiness Analysis Error:', error);
      toast.error('Failed to analyze AI readiness');
    } finally {
      setIsLoading(false);
    }
  };

  const applyRecommendation = async (recommendation: Recommendation) => {
    try {
      setIsApplying(recommendation.title);
      await api.post('/ai-readiness/apply-recommendation', {
        pageId,
        recommendation,
        action: 'add'
      });
      toast.success(`Applied: ${recommendation.title}`);
      // Re-analyze to get updated score
      await analyzePage();
    } catch (error) {
      console.error('Apply Recommendation Error:', error);
      toast.error(`Failed to apply: ${recommendation.title}`);
    } finally {
      setIsApplying(null);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-success';
    if (score >= 70) return 'text-warning';
    if (score >= 50) return 'text-info';
    return 'text-danger';
  };

  const getScoreBadge = (score: number) => {
    if (score >= 90) return 'badge-success';
    if (score >= 70) return 'badge-warning';
    if (score >= 50) return 'badge-info';
    return 'badge-danger';
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const getEffortColor = (effort: string) => {
    switch (effort) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'danger';
      default: return 'secondary';
    }
  };

  if (isLoading) {
    return (
      <div className="ai-readiness-panel">
        <div className="panel-header">
          <h5><i className="fas fa-robot me-2"></i>AI Readiness Analysis</h5>
          <button className="btn-close" onClick={onClose}></button>
        </div>
        <div className="panel-body text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 mb-0">Analyzing AI readiness...</p>
        </div>
      </div>
    );
  }

  if (!analysis || !analysis.success) {
    return (
      <div className="ai-readiness-panel">
        <div className="panel-header">
          <h5><i className="fas fa-robot me-2"></i>AI Readiness Analysis</h5>
          <button className="btn-close" onClick={onClose}></button>
        </div>
        <div className="panel-body text-center py-4">
          <i className="fas fa-exclamation-triangle text-warning fa-3x mb-3"></i>
          <p className="text-muted">Failed to analyze AI readiness</p>
          <button className="btn btn-primary" onClick={analyzePage}>
            <i className="fas fa-redo me-1"></i>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-readiness-panel">
      <div className="panel-header">
        <h5><i className="fas fa-robot me-2"></i>AI Readiness Analysis</h5>
        <button className="btn-close" onClick={onClose}></button>
      </div>
      
      <div className="panel-body">
        {/* Score Display */}
        <div className="score-section mb-4">
          <div className="row">
            <div className="col-md-6">
              <div className="score-card text-center p-3 border rounded">
                <h3 className={`mb-2 ${getScoreColor(analysis.score)}`}>
                  {analysis.score}/100
                </h3>
                <span className={`badge ${getScoreBadge(analysis.score)} fs-6`}>
                  {analysis.score >= 90 ? 'Excellent' : 
                   analysis.score >= 70 ? 'Good' : 
                   analysis.score >= 50 ? 'Needs Improvement' : 'Poor'}
                </span>
                <p className="text-muted mt-2 mb-0">AI Readiness Score</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="stats-card p-3 border rounded">
                <h6 className="mb-3">Analysis Summary</h6>
                <div className="row text-center">
                  <div className="col-6">
                    <div className="text-danger">
                      <strong>{analysis.analysis.criticalIssues}</strong>
                      <br />
                      <small>Critical</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-warning">
                      <strong>{analysis.analysis.highPriority}</strong>
                      <br />
                      <small>High Priority</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page Info */}
        <div className="page-info mb-4">
          <h6><i className="fas fa-info-circle me-2"></i>Page Information</h6>
          <div className="row">
            <div className="col-md-6">
              <p><strong>Title:</strong> {pageTitle}</p>
              <p><strong>URL:</strong> <a href={pageUrl} target="_blank" rel="noopener noreferrer">{pageUrl}</a></p>
            </div>
            <div className="col-md-6">
              <p><strong>Content Length:</strong> {analysis.pageInfo?.contentLength?.toLocaleString()} characters</p>
              <p><strong>Images:</strong> {analysis.pageInfo?.imageCount || 0}</p>
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="recommendations">
          <h6><i className="fas fa-lightbulb me-2"></i>Recommendations ({analysis.recommendations.length})</h6>
          
          {analysis.recommendations.length === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-check-circle text-success fa-3x mb-3"></i>
              <p className="text-muted">No recommendations found. Your page is well optimized!</p>
            </div>
          ) : (
            <div className="recommendations-list">
              {analysis.recommendations.map((recommendation, index) => (
                <div key={index} className="recommendation-card border rounded p-3 mb-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="mb-1">{recommendation.title}</h6>
                    <div className="d-flex gap-2">
                      <span className={`badge bg-${getPriorityColor(recommendation.priority)}`}>
                        {recommendation.priority}
                      </span>
                      <span className={`badge bg-${getEffortColor(recommendation.effort)}`}>
                        {recommendation.effort} effort
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-muted mb-2">{recommendation.description}</p>
                  
                  <div className="mb-2">
                    <strong>Impact:</strong> {recommendation.impact}
                  </div>
                  
                  <div className="mb-3">
                    <strong>Implementation:</strong>
                    <div className="bg-light p-2 rounded mt-1">
                      <code className="small">{recommendation.implementation}</code>
                    </div>
                  </div>
                  
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => applyRecommendation(recommendation)}
                      disabled={isApplying === recommendation.title}
                    >
                      {isApplying === recommendation.title ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                          Applying...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-magic me-1"></i>
                          Apply
                        </>
                      )}
                    </button>
                    <button className="btn btn-sm btn-outline-secondary">
                      <i className="fas fa-info-circle me-1"></i>
                      Learn More
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="actions mt-4 pt-3 border-top">
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary" onClick={analyzePage}>
              <i className="fas fa-redo me-1"></i>
              Re-analyze
            </button>
            <button className="btn btn-outline-secondary" onClick={onClose}>
              <i className="fas fa-times me-1"></i>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIReadinessPanel;
